import {
	Directive,
	ElementRef,
	inject,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
} from '@angular/core'
import { fromEvent, merge, Observable, Subject, takeUntil } from 'rxjs'

@Directive({
	selector: '[appClickOutside]',
})
export class ClickOutsideDirective implements OnInit, OnDestroy {
	protected readonly sourceIgnoreSelectors: string[] = [
		'.t-dialog',
		'.tui-data-list',
		'.sl-tp-controls',
		'.dropdown-content-root',
		'tui-data-list',
	]

	public ignoreSelectors: InputSignal<string[]> = input<string[]>([])
	public clickOutside: OutputEmitterRef<MouseEvent> = output<MouseEvent>()

	private readonly _elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef)

	private _mouseDownInside: boolean = false
	private _destroy$: Subject<void> = new Subject<void>()

	private _listenMouseEvents(): void {
		const events: Observable<MouseEvent> = merge(
			fromEvent<MouseEvent>(document, 'mousedown'),
			fromEvent<MouseEvent>(document, 'mouseup'),
		)

		events.pipe(takeUntil(this._destroy$)).subscribe((event: MouseEvent): void => {
			event.type === 'mousedown' ? this._handleMouseDown(event) : this._handleMouseUp(event)
		})
	}

	private _handleMouseDown(event: MouseEvent): void {
		const target: EventTarget | null = event.target
		if (!(target instanceof HTMLElement)) return

		const mergedIgnoreSelectors: string[] = [...this.sourceIgnoreSelectors, ...this.ignoreSelectors()]
		const insideEl: boolean = this._elementRef.nativeElement.contains(target)

		this._mouseDownInside =
			insideEl || mergedIgnoreSelectors.some((selector: string): boolean => !!target.closest(selector))
	}

	private _handleMouseUp(event: MouseEvent): void {
		const target: EventTarget | null = event.target
		if (!(target instanceof HTMLElement)) {
			this._mouseDownInside = false
			return
		}

		const mergedIgnoreSelectors: string[] = [...this.sourceIgnoreSelectors, ...this.ignoreSelectors()]
		const insideEl: boolean = this._elementRef.nativeElement.contains(target)
		const foundRequiredIgnoreElements: boolean = mergedIgnoreSelectors.some(
			(selector: string): boolean => !!target.closest(selector),
		)

		if (!insideEl && !foundRequiredIgnoreElements && !this._mouseDownInside) {
			this.clickOutside.emit(event)
		}

		this._mouseDownInside = false
	}

	ngOnInit(): void {
		this._listenMouseEvents()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
